import {
  Share2, Key, Trash2, Download, Edit3, FolderInput, Copy, Palette, Info, Link, UserX, Building2,
} from '../../icons';
import type { NodePublic } from '../../api/workspace';
import { tWorkspace } from '../../lib/workspace-i18n';
import { WorkspacePopover, PopoverItem, PopoverSep } from './WorkspacePopover';

/**
 * 행 메뉴 (2026-08-14) — 사용자 요구 10종:
 *
 *   "공유, 엑세스관리, 삭제, 다운로드, 이름바꾸기, 다음으로 이동, 다음으로 복사, 복제,
 *    폴더 색상, 세부정보"
 *
 * 순서는 **성격별로 묶어** 재배열했다(공유 / 가져오기 / 옮기기 / 정보·삭제). 요구된 나열
 * 순서 그대로 두면 '삭제' 가 위쪽 한가운데에 놓여 오조작 위험이 크다 — 파괴적 항목은 맨
 * 아래 + 구분선 뒤로 뺀다.
 *
 * 항목 노출은 **권한과 종류에 따라 달라진다**:
 *   - 폴더 색상 → 폴더에만
 *   - 회사 공유 폴더 안에서 일반 사원 → 다운로드 · 내 워크스페이스로 복사 · 세부정보만
 *   - 공유·이름변경·이동·삭제 → 소유자만
 */

export type RowAction =
  | 'share' | 'access' | 'download' | 'copy-link'
  | 'rename' | 'move' | 'copy-to' | 'duplicate'
  | 'color' | 'detail' | 'delete' | 'unshare'
  // 2026-08-14 — 회사공유 등록·해제(관리자 전용). 파일을 옮기지 않고 전사 공유만 건다.
  | 'company-register' | 'company-unregister';

interface Props {
  node: NodePublic;
  anchor: HTMLElement;
  /** 이 항목을 내가 소유했는가 — 서버 판정(detail.access)이 오기 전 목록 기준 근사. */
  canManage: boolean;
  /** 회사 공유 폴더 트리 안인가 — 일반 사원은 읽기 전용이다. */
  readOnly: boolean;
  /**
   * '내가 공유함' 목록인가 — 그 자리에서 바로 해제할 수 있어야 한다는 요구.
   * 모달을 거치지 않는 빠른 경로이고, 세부 조정은 기존 `액세스 관리` 가 맡는다.
   */
  showUnshare?: boolean;
  /**
   * 회사공유 등록 (2026-08-14) — **관리자만**. 회사공유는 관리자가 등록한 것만 보이므로
   * 일반 사원에게는 이 항목 자체가 없다(눌러 보고 나서 알게 되지 않도록).
   */
  showCompanyRegister?: boolean;
  /** 이미 등록된 항목 — 같은 자리에서 내릴 수 있어야 한다. */
  showCompanyUnregister?: boolean;
  onAction: (action: RowAction) => void;
  onClose: () => void;
}

export function WorkspaceRowMenu({
  node, anchor, canManage, readOnly, showUnshare = false,
  showCompanyRegister = false, showCompanyUnregister = false,
  onAction, onClose,
}: Props) {
  const isFolder = node.type === 'folder';
  const pick = (a: RowAction) => () => { onAction(a); onClose(); };

  return (
    <WorkspacePopover
      anchor={anchor}
      onClose={onClose}
      align="right"
      width={216}
      ariaLabel={tWorkspace('workspace.row.aria_menu', { name: node.name })}
    >
      {readOnly ? (
        <>
          {/* 회사 자료를 받아 가는 두 갈래 + 정보. 쓰기 계열은 서버가 어차피 막는다. */}
          {!isFolder && (
            <PopoverItem icon={Download} label={tWorkspace('workspace.row.download')} onClick={pick('download')} />
          )}
          {!isFolder && (
            <PopoverItem icon={Copy} label={tWorkspace('workspace.row.copy_to_mine')} onClick={pick('copy-to')} />
          )}
          <PopoverItem icon={Info} label={tWorkspace('workspace.row.detail')} onClick={pick('detail')} />
        </>
      ) : (
        <>
          {canManage && (
            <>
              <PopoverItem icon={Share2} label={tWorkspace('workspace.row.share')} onClick={pick('share')} />
              <PopoverItem icon={Key} label={tWorkspace('workspace.row.access')} onClick={pick('access')} />
              {/* '내가 공유함' 에서는 목록을 떠나지 않고 바로 끊을 수 있어야 한다. */}
              {showUnshare && (
                <PopoverItem icon={UserX} label={tWorkspace('workspace.row.unshare')} onClick={pick('unshare')} />
              )}
              {showCompanyRegister && (
                <PopoverItem
                  icon={Building2}
                  label={tWorkspace('workspace.company.register')}
                  onClick={pick('company-register')}
                />
              )}
              {showCompanyUnregister && (
                <PopoverItem
                  icon={Building2}
                  label={tWorkspace('workspace.company.unregister')}
                  onClick={pick('company-unregister')}
                />
              )}
              <PopoverSep />
            </>
          )}

          {!isFolder && (
            <PopoverItem icon={Download} label={tWorkspace('workspace.row.download')} onClick={pick('download')} />
          )}
          <PopoverItem icon={Link} label={tWorkspace('workspace.row.copy_link')} onClick={pick('copy-link')} />

          {canManage && (
            <>
              <PopoverSep />
              <PopoverItem icon={Edit3} label={tWorkspace('workspace.row.rename')} onClick={pick('rename')} />
              <PopoverItem icon={FolderInput} label={tWorkspace('workspace.row.move')} onClick={pick('move')} />
              <PopoverItem icon={Copy} label={tWorkspace('workspace.row.copy_to')} onClick={pick('copy-to')} />
              {/* 복제는 파일만 — 폴더 복사는 서버가 아직 거절한다(하위 전체 복제 미지원). */}
              {!isFolder && (
                <PopoverItem icon={Copy} label={tWorkspace('workspace.row.duplicate')} onClick={pick('duplicate')} />
              )}
              {isFolder && (
                <PopoverItem icon={Palette} label={tWorkspace('workspace.row.color')} onClick={pick('color')} />
              )}
            </>
          )}

          <PopoverSep />
          <PopoverItem icon={Info} label={tWorkspace('workspace.row.detail')} onClick={pick('detail')} />
          {canManage && (
            <PopoverItem
              icon={Trash2}
              label={tWorkspace('workspace.row.delete')}
              onClick={pick('delete')}
              danger
            />
          )}
        </>
      )}
    </WorkspacePopover>
  );
}
